import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { CheckCircle2, Circle, Mail, RefreshCw, Store as StoreIcon, Filter, Loader2 } from 'lucide-react';
import { API } from '../App';

const STEP_LABELS = {
  approved: 'Approved',
  covenant_signed: 'Covenant signed',
  kyc_approved: 'KYC verified',
  stripe_connected: 'Stripe connected',
  first_product_uploaded: 'First product',
};

const FILTERS = [
  { value: 'all', label: 'All vendors' },
  { value: 'blocked', label: 'Stuck / incomplete' },
  { value: 'complete', label: 'Fully onboarded' },
];

function StepBadge({ ok, label, testid }) {
  const Icon = ok ? CheckCircle2 : Circle;
  return (
    <div
      className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-700' : 'text-muted-foreground'}`}
      data-testid={testid}
    >
      <Icon className={`h-3.5 w-3.5 ${ok ? 'fill-green-100 stroke-green-700' : 'stroke-current'}`} />
      <span className={ok ? 'font-medium' : ''}>{label}</span>
    </div>
  );
}

export default function VendorOnboardingAdmin() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('blocked');
  const [search, setSearch] = useState('');
  const [nudgingId, setNudgingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/vendors/onboarding-status`, { params: { filter } });
      setRows(r.data?.vendors || []);
      setSummary(r.data?.summary || null);
    } catch (_e) {
      toast.error('Failed to load onboarding status');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const sendNudge = async (vendorId, businessName) => {
    if (!window.confirm(`Send an onboarding nudge email to "${businessName}"?`)) return;
    setNudgingId(vendorId);
    try {
      const r = await axios.post(`${API}/admin/vendors/${vendorId}/send-onboarding-nudge`);
      toast.success(r.data?.message || 'Nudge sent');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send nudge');
    } finally {
      setNudgingId(null);
    }
  };

  const filtered = rows.filter((v) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (v.business_name || '').toLowerCase().includes(q) ||
      (v.email || '').toLowerCase().includes(q) ||
      (v.contact_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="vendor-onboarding-admin">
      {/* Summary strip */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card data-testid="onboarding-summary-total">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Total vendors</div>
            <div className="text-3xl font-medium mt-1">{summary?.total_vendors ?? '—'}</div>
          </CardContent>
        </Card>
        <Card data-testid="onboarding-summary-complete">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-green-700">Fully onboarded</div>
            <div className="text-3xl font-medium mt-1 text-green-700">{summary?.complete ?? '—'}</div>
          </CardContent>
        </Card>
        <Card data-testid="onboarding-summary-blocked">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-orange-600">Stuck / incomplete</div>
            <div className="text-3xl font-medium mt-1 text-orange-600">{summary?.blocked ?? '—'}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <StoreIcon className="h-5 w-5" /> Vendor Onboarding Checklist
            </CardTitle>
            <CardDescription>
              5-step onboarding progress per vendor. Send a nudge email to anyone who&rsquo;s stuck.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Filter className="h-4 w-4 text-muted-foreground" />
              {FILTERS.map((f) => (
                <Button
                  key={f.value}
                  variant={filter === f.value ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setFilter(f.value)}
                  data-testid={`onboarding-filter-${f.value}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="onboarding-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Search by business name, email, or contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4 max-w-md"
            data-testid="onboarding-search"
          />

          {loading ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading vendors…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="onboarding-empty">
              {filter === 'complete'
                ? 'No fully-onboarded vendors yet.'
                : filter === 'blocked'
                  ? 'Every vendor is fully onboarded. 🎉'
                  : 'No vendors yet — invite some via the Founding Vendor program.'}
            </div>
          ) : (
            <div className="space-y-3" data-testid="onboarding-rows">
              {filtered.map((v) => (
                <div
                  key={v.vendor_id}
                  className="border border-border rounded-xl p-4 bg-background hover:border-primary/30 transition"
                  data-testid={`onboarding-row-${v.vendor_id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium truncate">{v.business_name}</h4>
                        {v.is_complete && <Badge className="bg-green-600 hover:bg-green-700">Complete</Badge>}
                        {!v.is_complete && (
                          <Badge variant="outline" className="border-orange-300 text-orange-700">
                            {v.completed_steps}/{v.total_steps} done
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 truncate">
                        {v.contact_name && <span>{v.contact_name} · </span>}
                        {v.email || '—'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">Progress</div>
                        <div className="w-32 h-2 bg-muted rounded-full overflow-hidden mt-1">
                          <div
                            className={`h-full transition-all ${v.is_complete ? 'bg-green-600' : 'bg-primary'}`}
                            style={{ width: `${v.completion_pct}%` }}
                          />
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sendNudge(v.vendor_id, v.business_name)}
                        disabled={v.is_complete || nudgingId === v.vendor_id || !v.email}
                        data-testid={`onboarding-nudge-${v.vendor_id}`}
                        title={v.is_complete ? 'Already complete' : !v.email ? 'No email on file' : 'Send reminder email'}
                      >
                        {nudgingId === v.vendor_id ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4 mr-1" />
                        )}
                        Nudge
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {Object.entries(STEP_LABELS).map(([key, label]) => (
                      <StepBadge
                        key={key}
                        ok={!!v.checks?.[key]}
                        label={label}
                        testid={`onboarding-step-${v.vendor_id}-${key}`}
                      />
                    ))}
                  </div>

                  {v.product_count > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {v.product_count} product{v.product_count === 1 ? '' : 's'} listed
                      {v.stripe_account_id && !v.stripe_payouts_enabled && (
                        <span className="ml-2 text-orange-600">
                          · Stripe account started but payouts not yet enabled
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
